import { h, mount, clear } from "@/lib/dom";
import { icon } from "@/lib/icons";
import { topicsService, pbErrorMessage } from "@/services";
import { pathService, IMPORTED_SOURCE } from "@/services/path.service";
import { topicSources } from "@/services/source.service";
import { markPracticed } from "@/services/streak.service";
import { recordQuizResult } from "@/services/learn-stats.service";
import { addErrorCard } from "@/services/error-deck.service";
import { lessonQuestions } from "@/features/pfad/generate";
import { mountQuestion, inline } from "@/features/pfad/render";
import { checkFreeTextWithAi, checkQuestion, verdictScore } from "@/features/pfad/scoring";
import { aiTroubleCard } from "@/features/learn/aiHealth";
import { pfRoot, pfHead, pfLoading, pfButton, pfEmpty, onEnter } from "@/features/pfad/shell";
import { AFB_LABEL, AFB_WEIGHT, type CheckResult, type Lesson, type Question } from "@/features/pfad/types";
import { navigate, type RouteContext } from "@/app/router";

/**
 * Eine Lektion des Lernpfads als Vollbild-Quiz.
 *
 * Ablauf je Aufgabe: antworten → prüfen → Feedback im farbigen Fußbalken →
 * weiter. Der Fußbalken ist der einzige Ort für Rückmeldung; er wechselt die
 * Farbe und den Knopf, damit man ohne Lesen sieht, woran man ist.
 *
 * Aufgaben werden beim ersten Start erzeugt und dann an der Lektion
 * gespeichert – ein zweiter Durchgang lädt sofort. Haben sich die Notizen
 * geändert (anderer Quellen-Hash), wird neu generiert.
 */
export async function LessonPage(ctx: RouteContext): Promise<HTMLElement> {
  const topicId = ctx.params.id;
  const lessonId = ctx.params.lesson;
  const toPath = () => navigate(`/topic/${topicId}/pfad`);

  const head = pfHead(toPath);
  const stage = h("div", { class: "pf-stage" });
  const foot = h("div", { class: "pf-foot" });
  const root = pfRoot(head.el, stage, foot);

  mount(stage, h("div", { class: "pf-body" }, pfLoading("Lektion wird geladen")));

  const [topic, lessons] = await Promise.all([
    topicsService.get(topicId).catch(() => null),
    pathService.listByTopic(topicId).catch(() => [] as Lesson[]),
  ]);
  const lesson = lessons.find((l) => l.id === lessonId);

  if (!topic || !lesson) {
    mount(stage, h("div", { class: "pf-body" }, pfEmpty("Lektion nicht gefunden", "Sie wurde vielleicht gelöscht.", pfButton("Zum Pfad", toPath, "gray"))));
    return root;
  }

  /* --------------------------- Aufgaben besorgen --------------------------- */

  let questions: Question[] = lesson.questions;
  if (lesson.source_hash !== IMPORTED_SOURCE) try {
    const src = await topicSources(topic);
    const stale = lesson.source_hash !== src.hash;
    if (!questions.length || stale) {
      mount(
        stage,
        h(
          "div",
          { class: "pf-body" },
          pfLoading(
            questions.length ? "Deine Notizen haben sich geändert" : "Aufgaben werden erstellt",
            `„${lesson.title}" – ausschließlich aus deinem eigenen Material.`
          )
        )
      );
      questions = await lessonQuestions(lesson, src, 9);
      if (questions.length) {
        await pathService.saveQuestions(lesson, questions, src.hash).catch(() => {});
      }
    }
  } catch (err) {
    mount(
      stage,
      h(
        "div",
        { class: "pf-body" },
        aiTroubleCard(pbErrorMessage(err)),
        h("div", { style: "margin-top:14px;display:flex;justify-content:center" }, pfButton("Zum Pfad", toPath, "ghost"))
      )
    );
    return root;
  }

  if (!questions.length) {
    mount(
      stage,
      h(
        "div",
        { class: "pf-body" },
        pfEmpty(
          "Keine Aufgaben erzeugt",
          "Zu dieser Lektion ließ sich aus deinem Material nichts Prüfbares bauen. Ergänze " +
            "die Mitschrift oder lade ein Dokument mit mehr Inhalt hoch.",
          pfButton("Zum Pfad", toPath, "gray")
        )
      )
    );
    return root;
  }

  /* ------------------------------- Ablauf ------------------------------- */

  let actionRef: HTMLButtonElement | null = null;
  let idx = 0;
  let wrong = 0;
  let scoreGot = 0;
  let scoreMax = 0;
  const startedAt = Date.now();

  function show(): void {
    if (idx >= questions.length) return finish();
    head.set(idx, wrong, questions.length);

    const q = questions[idx];
    const mounted = mountQuestion(q, () => updateAction());
    let checked = false;

    const afbClass = q.type === "info" ? "info" : q.afb === 2 ? "a2" : q.afb === 3 ? "a3" : "";
    const tag = h("span", { class: `pf-tag ${afbClass}` }, q.type === "info" ? "Wissen" : AFB_LABEL[q.afb]);

    mount(stage, h("div", { class: "pf-body" }, tag, mounted.el));
    stage.scrollTop = 0;

    const actionBtn = h("button", { class: "pf-btn block", type: "button" },
      q.type === "info" ? "Verstanden" : "Prüfen") as HTMLButtonElement;
    actionRef = actionBtn;
    const fbSlot = h("div");
    mount(foot, h("div", { class: "pf-foot-in" }, fbSlot, actionBtn));
    foot.className = "pf-foot";

    function updateAction(): void {
      if (checked) return;
      actionBtn.disabled = !mounted.ready();
    }
    updateAction();

    actionBtn.onclick = async () => {
      if (checked) {
        idx++;
        show();
        return;
      }
      if (q.type === "info") {
        idx++;
        show();
        return;
      }
      if (!mounted.ready()) return;

      const answer = mounted.read();
      let res: CheckResult;
      if (q.type === "code" && q.afb === 3) {
        actionBtn.disabled = true;
        actionBtn.textContent = "Wird geprüft …";
        res = await checkFreeTextWithAi(q, answer.text ?? "");
      } else {
        res = checkQuestion(q, answer);
      }
      checked = true;
      mounted.reveal(res);
      mounted.lock();

      const weight = AFB_WEIGHT[q.afb];
      scoreGot += verdictScore(res.verdict) * weight;
      scoreMax += weight;
      if (res.verdict === "wrong") {
        wrong++;
        addErrorCard(topicId, q.question, res.detail ?? res.title);
      }

      head.set(idx + 1, wrong, questions.length);
      showFeedback(res, q);
      actionBtn.textContent = idx + 1 >= questions.length ? "Ergebnis" : "Weiter";
      actionBtn.disabled = false;
    };

    function showFeedback(res: CheckResult, question: Question): void {
      foot.className =
        "pf-foot " + (res.verdict === "correct" ? "correct" : res.verdict === "partial" ? "tip" : "wrong");
      const mark = res.verdict === "correct" ? "✓" : res.verdict === "partial" ? "~" : "✗";
      mount(
        fbSlot,
        h(
          "div",
          { class: "pf-fb" },
          h("div", { class: "pf-fb-badge" }, mark),
          h(
            "div",
            { class: "pf-fb-main" },
            h("h4", { html: inline(res.title) }),
            res.detail ? h("p", { html: inline(res.detail) }) : null,
            question.reference
              ? h(
                  "div",
                  { class: "pf-ref" },
                  "Fundstelle: ",
                  h("b", null, question.reference.label),
                  question.reference.snippet ? ` – „${question.reference.snippet}"` : ""
                )
              : null
          )
        )
      );
    }
  }

  /* ------------------------------ Abschluss ------------------------------ */

  function finish(): void {
    head.set(questions.length, wrong, questions.length);
    const graded = questions.filter((q) => q.type !== "info").length;
    const pct = scoreMax > 0 ? Math.round((scoreGot / scoreMax) * 100) : 100;
    const xp = Math.max(5, Math.round((pct / 100) * (10 + graded * 3)));
    const minutes = Math.max(1, Math.round((Date.now() - startedAt) / 60000));

    markPracticed();
    void recordQuizResult(topicId, graded - wrong, graded);
    // Muss stehen, BEVOR "Weiter im Pfad" zurücknavigiert – sonst sieht die
    // Pfad-Seite (frischer `listByTopic`-Aufruf) noch den alten Stand und
    // schaltet die nächste Lektion nicht frei.
    const recorded = pathService.recordResult(lesson!, pct, xp).catch(() => {});

    const crown = pct >= 80 ? "gold" : pct >= 50 ? "green" : "blue";
    mount(
      stage,
      h(
        "div",
        { class: "pf-body" },
        h(
          "div",
          { class: "pf-result" },
          h("div", { class: `pf-crown ${crown}`, html: icon(pct >= 80 ? "award" : "target", 44) }),
          h("h1", null, pct >= 80 ? "Lektion gemeistert!" : pct >= 50 ? "Geschafft!" : "Durch – aber übe nochmal"),
          h(
            "div",
            { class: "pf-cards" },
            rcard("gold", "XP", `+${xp}`),
            rcard("green", "Treffer", `${pct}%`),
            rcard("blue", "Aufgaben", String(graded)),
            rcard("orange", "Zeit", `${minutes} min`)
          ),
          h(
            "div",
            { style: "display:flex;flex-direction:column;gap:10px" },
            pfButton("Weiter im Pfad", () => void recorded.then(toPath)),
            pfButton("Lektion wiederholen", () => restart(), "gray")
          )
        )
      )
    );
    clear(foot);
  }

  function rcard(color: string, label: string, value: string): HTMLElement {
    return h(
      "div",
      { class: `pf-rcard c-${color}` },
      h("div", { class: "in" }, h("div", { class: "lab" }, label), h("div", { class: "val" }, value))
    );
  }

  function restart(): void {
    idx = 0;
    wrong = 0;
    scoreGot = 0;
    scoreMax = 0;
    show();
  }

  onEnter(root, () => { if (!actionRef?.disabled) actionRef?.click(); });
  show();
  return root;
}
